import { apiQueryKey } from "@/api/query-client";
import useUserPreferences from "@/hooks/common/user/use-user-preferences";
import { Preferences } from "@/shared/enums";
import { useApiSession } from "@Context/common/general/api-session-context";
import { AppContext, Events } from "@Context/common/general/context";
import ChallengeStatus from "@Enums/ChallengeStatus";
import GameModes from "@Enums/GameModes";
import ScoreFieldTypes from "@Enums/ScoreFieldTypes";
import ScoreTypes from "@Enums/ScoreTypes";
import { UserRankResult } from "@Globals/types";
import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import useApiQuery from "@hooks/common/general/use-api-query";
import { Api } from "@Shared/schemas";
import { ChallengeResponse, ChallengeResultsResponse, ChallengeSession, ChallengeSessionReward } from "@Shared/schemas/challenges/challengeSchemas";
import { useQueryClient } from "@tanstack/react-query";
import { uniqBy } from "lodash-es";
import { useCallback, useContext, useMemo } from "react";

export interface IUseChallengeData {
  id: number;
  overrideData?: ChallengeResponse | null;
}

export interface UseChallengeDataResponse {
  challengeDetails: ChallengeResponse | null;
  challengeStatus?: ChallengeStatus;
  leaderboardData: UserRankResult[];
  loadingLeaderboard?: boolean;
  leaderboardError?: Error | null;
  retryLeaderboard: () => void;
  userLeaderboardData: UserRankResult | undefined;
  challengeResults: ChallengeResultsResponse | null;
  loadingChallengeResults?: boolean;
  challengeResultsError: Error | null;
  retryChallengeResults: () => void;
  achievedRewards: { id: number; created_at: string }[];
  bestResult: ChallengeSession | null;
  latestResult: ChallengeSession | null;
  latestReward: ChallengeSessionReward | null;
  userInShortList?: boolean;
  leaderboardVisibility?: boolean;
  canSeeLeaderboard?: boolean;
  loadingLeaderboardVisibility?: boolean;
  fetchLeaderboard: (skipPreferenceCheck?: boolean) => void;
  fetchUserLeaderboard: () => void;
  joinLeaderboard: () => void;
  onChallengeResultsFetched: (data: ChallengeResultsResponse | null, callBack?: () => void) => void;
}

export function useChallengeData({ id, overrideData }: IUseChallengeData): UseChallengeDataResponse {
  const { state } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { preferences, isPending: loadingLeaderboardVisibility } = useUserPreferences();
  const leaderboardVisibility = !!preferences?.[Preferences.leaderboard_visibility];
  const canSeeLeaderboard = leaderboardVisibility;
  const firebaseUid = useApiSession();

  const leaderboardRequest = {
    score_field: ScoreFieldTypes.BYB_IQ,
    score_type: ScoreTypes.MAXIMUM,
    challenge_id: id,
    is_completed: true,
    game_mode: GameModes.SCENARIO,
  };
  const enabled = Boolean(firebaseUid && state.user.id && Number.isFinite(id) && id > 0);

  const leaderboardQuery = useApiQuery({
    api: Api.Stats.GetLeaderboard,
    request: { ...leaderboardRequest, limit: 10 },
    queryKey: [state.user.id],
    enabled,
    disableLoader: true,
  });

  const userLeaderboardQuery = useApiQuery({
    api: Api.Stats.GetLeaderboard,
    request: { ...leaderboardRequest, user_id: state.user.id ?? undefined },
    queryKey: [state.user.id],
    enabled,
    disableLoader: true,
  });

  const leaderboardData = useMemo<UserRankResult[]>(
    () => (leaderboardQuery.data ?? []).map(player => ({ ...player, avatar: player.avatar ?? undefined })),
    [leaderboardQuery.data]
  );

  const userResult = userLeaderboardQuery.data?.[0];
  const userLeaderboardData = useMemo(() => (userResult ? { ...userResult, avatar: userResult.avatar ?? undefined } : undefined), [userResult]);
  const userInShortList = leaderboardData.some(player => player.doc_id === state.user.id);
  const loadingLeaderboard = leaderboardQuery.isPending || userLeaderboardQuery.isPending;
  const leaderboardError = leaderboardQuery.error ?? userLeaderboardQuery.error;
  const { refetch: refetchLeaderboard } = leaderboardQuery;
  const { refetch: refetchUserLeaderboard } = userLeaderboardQuery;

  const fetchLeaderboard = useCallback(() => {
    if (enabled) void refetchLeaderboard();
  }, [enabled, refetchLeaderboard]);

  const fetchUserLeaderboard = useCallback(() => {
    if (enabled) void refetchUserLeaderboard();
  }, [enabled, refetchUserLeaderboard]);

  const retryLeaderboard = useCallback(() => {
    fetchLeaderboard();
    fetchUserLeaderboard();
  }, [fetchLeaderboard, fetchUserLeaderboard]);

  const overrideHasChallengeData = overrideData?.id;

  const { challenges } = useChallengesQuery(
    {
      ids: [+id],
      unpublished_allowed: state.user.isSuperAdmin,
      include_fields: ["scenarios", "targets", "rewards"],
    },
    { enabled }
  );

  const challengeDetails = overrideHasChallengeData ? overrideData : (challenges[0] ?? null);

  const { data, isPending, error, refetch } = useApiQuery({
    api: Api.Challenges.GetChallengeResults,
    request: {
      user_id: state.user.id!,
      challenge_id: id,
    },
    queryKey: [state.user.id],
    enabled,
    disableLoader: true,
  });

  const challengeResults = data?.data ?? null;
  const hasChallengeResults = data !== undefined;
  const loadingChallengeResults = isPending;
  const challengeResultsError = error;

  const retryChallengeResults = useCallback(() => {
    if (enabled) void refetch();
  }, [enabled, refetch]);

  const { achievedRewards, bestResult, latestResult, latestReward, challengeStatus } = useMemo(() => {
    if (!hasChallengeResults) {
      return { achievedRewards: [], bestResult: null, latestResult: null, latestReward: null, challengeStatus: undefined };
    }
    const sessions = challengeResults?.challenge_sessions ?? [];
    if (!sessions.length) {
      return { achievedRewards: [], bestResult: null, latestResult: null, latestReward: null, challengeStatus: ChallengeStatus.NOT_STARTED };
    }

    const bestResult = sessions.reduce((best, session) => ((session.results.byb_iq ?? -1) > (best.results.byb_iq ?? -1) ? session : best));
    // Reduce rather than sorting the array held in the shared query cache.
    const latestResult = sessions.reduce((latest, session) =>
      new Date(session.play_session.created_at).getTime() > new Date(latest.play_session.created_at).getTime() ? session : latest
    );
    const latestReward = latestResult.rewards?.find(reward => reward.id === latestResult.targets?.reward_id) ?? null;
    const achievedRewards = uniqBy(
      sessions.flatMap(session => (session.rewards ?? []).map(({ id, created_at }) => ({ id, created_at: new Date(created_at).toISOString() }))),
      reward => reward.id
    );
    const hasCompletedSession = sessions.some(session => session.status === ChallengeStatus.COMPLETED);
    const challengeStatus = !hasCompletedSession
      ? ChallengeStatus.FAILED
      : userLeaderboardData?.rank === 1
        ? ChallengeStatus.WINNER
        : ChallengeStatus.COMPLETED;

    return { achievedRewards, bestResult, latestResult, latestReward, challengeStatus };
  }, [challengeResults, hasChallengeResults, userLeaderboardData?.rank]);

  const onChallengeResultsFetched = useCallback(
    (data: ChallengeResultsResponse | null, callBack?: () => void) => {
      if (!enabled) return;
      queryClient.setQueryData(
        apiQueryKey(Api.Challenges.GetChallengeResults.Name, firebaseUid, [state.user.id], { user_id: state.user.id, challenge_id: id }),
        {
          code: "ok",
          data,
        }
      );
      callBack?.();
    },
    [queryClient, enabled, firebaseUid, state.user.id, id]
  );

  const joinLeaderboard = useCallback(() => {
    Events.app.navigate.emit({ path: `/settings/privacy` });
  }, []);

  return useMemo(
    () => ({
      challengeDetails,
      challengeStatus,
      leaderboardData,
      loadingLeaderboard,
      leaderboardError,
      retryLeaderboard,
      userLeaderboardData,
      challengeResults,
      loadingChallengeResults,
      challengeResultsError,
      retryChallengeResults,
      achievedRewards,
      bestResult,
      latestResult,
      latestReward,
      userInShortList,
      leaderboardVisibility,
      canSeeLeaderboard,
      loadingLeaderboardVisibility,
      fetchLeaderboard,
      fetchUserLeaderboard,
      onChallengeResultsFetched,
      joinLeaderboard,
    }),
    [
      challengeDetails,
      challengeStatus,
      overrideHasChallengeData,
      overrideData,
      leaderboardData,
      loadingLeaderboard,
      leaderboardError,
      retryLeaderboard,
      userLeaderboardData,
      challengeResults,
      loadingChallengeResults,
      challengeResultsError,
      retryChallengeResults,
      achievedRewards,
      bestResult,
      latestResult,
      latestReward,
      userInShortList,
      leaderboardVisibility,
      canSeeLeaderboard,
      loadingLeaderboardVisibility,
      fetchLeaderboard,
      fetchUserLeaderboard,
      onChallengeResultsFetched,
      joinLeaderboard,
    ]
  );
}
